import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { STATUSES, SECTORS, POCS, inr, pct, xx } from '@krit/core';
import { listDeals, patchDeal, getMe, canEdit, type DealDto } from '../lib/api.js';
import { PageHeader, StatTile, Skeleton } from '../components/ui.js';
import { ChatWidget } from '../components/ChatWidget.js';
import { CrmBoard, isClosedStage } from '../components/CrmBoard.js';
import { STAGE_DOT } from '../lib/stages.js';

const STATUS_PILL: Record<string, string> = {
  Open: 'bg-[#eef1f5] text-muted',
  'Under Review': 'bg-teal-50 text-teal-d',
  Interested: 'bg-green-50 text-green',
  'In Discussion': 'bg-purple-50 text-purple',
  Diligence: 'bg-amber-50 text-amber',
  'IC Review': 'bg-purple-50 text-purple',
  'Term Sheet': 'bg-green-50 text-green',
  Invested: 'bg-teal-50 text-teal-d',
  'Not Interested': 'bg-red-50 text-red',
  Intake: 'bg-gold-50 text-gold',
};
const GRADE_PILL: Record<string, string> = {
  green: 'bg-green-50 text-green',
  teal: 'bg-teal-50 text-teal-d',
  amber: 'bg-amber-50 text-amber',
  red: 'bg-red-50 text-red',
};
const FIT_PILL: Record<string, string> = {
  Fit: 'bg-green-50 text-green',
  Borderline: 'bg-amber-50 text-amber',
  Pass: 'bg-red-50 text-red',
};
function gradeColor(g: string): string {
  if (g === 'A+' || g === 'A') return 'green';
  if (g === 'A−') return 'teal';
  if (g === 'B+' || g === 'B') return 'amber';
  return 'red';
}

const PIPELINE_STATUSES = STATUSES.filter((s) => s !== 'Intake');

/** Groups run in pipeline order; anything with an unknown status falls in after them. */
const GROUP_ORDER: string[] = [...STATUSES];
/** Column count of the table — group header rows span all of it. */
const COLS = 25;

const VIEW_KEY = 'krit.deals.view';
const GROUP_KEY = 'krit.deals.group';

const TH = ({ children, r }: { children?: ReactNode; r?: boolean }) => (
  <th className={`bg-navy text-[#dce6f4] px-3 py-2.5 text-[10.5px] font-bold uppercase whitespace-nowrap ${r ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);

export function Deals() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['deals'], queryFn: listDeals, retry: 2, retryDelay: 800 });
  const all = useMemo(() => data?.deals ?? [], [data]);

  const [status, setStatus] = useState('All');
  const [sector, setSector] = useState('All');
  const [poc, setPoc] = useState('All');
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  // Pick up a search coming from the Topbar (/deals?q=…), including repeat searches.
  useEffect(() => { setQ(searchParams.get('q') ?? ''); }, [searchParams]);
  // Board vs table is a working preference — remember it across visits.
  const [view, setView] = useState<'table' | 'board'>(() => (localStorage.getItem(VIEW_KEY) === 'board' ? 'board' : 'table'));
  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);
  // Table rows are grouped under status headers by default; "none" gives the old flat list back.
  const [group, setGroup] = useState<'status' | 'none'>(() => (localStorage.getItem(GROUP_KEY) === 'none' ? 'none' : 'status'));
  useEffect(() => { localStorage.setItem(GROUP_KEY, group); }, [group]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = (s: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (!next.delete(s)) next.add(s);
    return next;
  });

  const { data: meData } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false, staleTime: 300_000 });
  // Only analyst+ may PATCH a deal, so viewers get the board read-only (no drag handles).
  const canEditDeals = canEdit(meData?.identity);

  const pocs = useMemo(() => [...new Set(all.map((d) => d.poc).filter(Boolean))], [all]);
  // Inline edits from the table (status + POC) — patch the deal and refresh the list. Editors only.
  const qc = useQueryClient();
  const patchM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof patchDeal>[1] }) => patchDeal(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });
  // POC dropdown options: the curated POCS list merged with any POC already on a deal (so nothing
  // disappears), sorted. Add names to POCS in packages/core/src/constants.ts.
  const pocOptions = useMemo(() => [...new Set([...POCS, ...pocs])].sort((a, b) => a.localeCompare(b)), [pocs]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter((d) => {
      if (status !== 'All' && d.status !== status) return false;
      if (sector !== 'All' && d.sector !== sector) return false;
      if (poc !== 'All' && d.poc !== poc) return false;
      if (ql && !`${d.company} ${d.sector} ${d.banker} ${d.poc} ${d.location}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [all, status, sector, poc, q]);

  // Status groups for the table, in pipeline order. Empty stages are dropped — a wide financial
  // table earns nothing from a row of headers with no deals under it.
  const groups = useMemo(() => {
    const by = new Map<string, DealDto[]>();
    for (const d of filtered) {
      const list = by.get(d.status);
      if (list) list.push(d); else by.set(d.status, [d]);
    }
    const known = GROUP_ORDER.filter((s) => by.has(s));
    const rest = [...by.keys()].filter((s) => !GROUP_ORDER.includes(s)).sort();
    return [...known, ...rest].map((s) => ({ status: s, deals: by.get(s)! }));
  }, [filtered]);

  // Tiles describe the *live* pipeline. Dropped and closed-won deals accumulate without bound
  // (most sourced deals end up Not Interested), so counting them here would drown the signal.
  const live = useMemo(() => all.filter((d) => !isClosedStage(d.status)), [all]);
  const fit = live.filter((d) => d.computed.thesisFit === 'Fit').length;
  const invested = all.filter((d) => d.status === 'Invested').length;
  const dropped = all.filter((d) => d.status === 'Not Interested').length;
  const avg = live.length ? Math.round(live.reduce((a, d) => a + d.computed.score, 0) / live.length) : 0;
  const totAlloc = live.reduce((a, d) => a + (d.allocation || 0), 0);

  function exportCSV() {
    const cols = ['#', 'Date', 'Company', 'Sector', 'Location', 'Status', 'Banker/MB', 'POC', 'Rev FY26', 'Rev FY25', 'PAT FY26', 'PAT FY25', 'Val ask', 'Round', 'Alloc', 'Impl PE', 'Peer PE now', 'Peer PE IPO', 'MoS %', 'IPO', 'Peers', 'Score', 'Fit'];
    const rows = [cols];
    filtered.forEach((d, i) => {
      const c = d.computed;
      rows.push([String(i + 1), d.dateCRM, d.company, d.sector, d.location, d.status, d.banker, d.poc, String(d.rev26), String(d.rev25), String(d.pat26), String(d.pat25), String(d.valuationAsk), String(d.totalRaise), String(d.allocation), c.entryPE?.toFixed(1) ?? 'n/m', String(c.peNow), String(c.peIPO), c.mos?.toFixed(1) ?? 'n/m', d.ipo, d.peers.join('; '), String(c.score), c.thesisFit]);
    });
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'krit_deals.csv';
    a.click();
  }

  /** One deal row. `i` is the ordinal within its group (or within the flat list when ungrouped). */
  function row(d: DealDto, i: number) {
    const c = d.computed;
    const mosColor = c.mos == null ? 'text-muted-2' : c.mos >= 30 ? 'text-green' : c.mos >= 15 ? 'text-amber' : 'text-red';
    return (
      <tr key={d.id} onClick={() => navigate(`/deals/${d.id}`)} className="cursor-pointer border-b border-line-2 hover:bg-teal-50">
        <td className="px-3 py-2.5 mono text-muted-2">{i + 1}</td>
        <td className="px-3 py-2.5">
          <span className="font-bold text-navy">{d.company}</span>
          <span className="block font-normal text-muted text-[10.5px]">{d.sector} · {d.location}</span>
        </td>
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          {canEditDeals ? (
            <select value={d.status} disabled={patchM.isPending}
              onChange={(e) => patchM.mutate({ id: d.id, patch: { status: e.target.value as (typeof STATUSES)[number] } })}
              title="Change status"
              className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-teal disabled:opacity-50 ${STATUS_PILL[d.status] ?? 'bg-[#eef1f5] text-muted'}`}>
              {STATUSES.map((s) => <option key={s} value={s} className="bg-white text-navy font-normal">{s}</option>)}
            </select>
          ) : (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold ${STATUS_PILL[d.status] ?? 'bg-[#eef1f5] text-muted'}`}>{d.status}</span>
          )}
        </td>
        <td className="px-3 py-2.5">{d.banker}</td>
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          {canEditDeals ? (
            <select value={d.poc || ''} disabled={patchM.isPending}
              onChange={(e) => patchM.mutate({ id: d.id, patch: { poc: e.target.value } })}
              title="Assign POC"
              className="text-[12px] bg-transparent border border-line rounded px-1.5 py-0.5 cursor-pointer max-w-[140px] focus:outline-none focus:ring-1 focus:ring-teal disabled:opacity-50">
              <option value="">— none —</option>
              {pocOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          ) : (d.poc || <span className="text-muted-2">—</span>)}
        </td>
        <td className="px-3 py-2.5 text-right mono">{inr(d.rev26)}</td>
        <td className="px-3 py-2.5 text-right mono text-muted">{inr(d.rev25)}</td>
        <td className="px-3 py-2.5 text-right mono">{inr(d.pat26)}</td>
        <td className="px-3 py-2.5 text-right mono text-muted">{inr(d.pat25)}</td>
        <td className="px-3 py-2.5 text-right mono">{inr(d.valuationAsk)}</td>
        <td className="px-3 py-2.5 text-right mono">{inr(d.totalRaise)}</td>
        <td className="px-3 py-2.5 text-right mono font-bold">{inr(d.allocation)}</td>
        <td className="px-3 py-2.5 text-right mono">{c.entryPE == null ? '—' : xx(c.entryPE)}</td>
        <td className="px-3 py-2.5 text-right mono">{xx(c.peNow)}</td>
        <td className="px-3 py-2.5 text-right mono">{xx(c.peIPO)}</td>
        <td className={`px-3 py-2.5 text-right mono font-bold ${mosColor}`}>{c.mos == null ? '—' : pct(c.mos)}</td>
        <td className="px-3 py-2.5">{d.ipo}</td>
        <td className="px-3 py-2.5 text-right"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold ${GRADE_PILL[gradeColor(c.grade)]}`}>{c.score}</span></td>
        <td className="px-3 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold ${GRADE_PILL[c.credit.color] ?? 'bg-[#eef1f5] text-muted'}`} title={c.credit.rationale}>{c.credit.grade}</span></td>
        <td className="px-3 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold ${FIT_PILL[c.thesisFit]}`}>{c.thesisFit}</span></td>
        <td className="px-3 py-2.5 max-w-[180px] overflow-hidden text-ellipsis" title={d.peers.join(', ')}>{d.peers.slice(0, 2).join(', ')}{d.peers.length > 2 ? ` +${d.peers.length - 2}` : ''}</td>
        <td className="px-3 py-2.5 max-w-[220px] overflow-hidden text-ellipsis" title={d.tailwinds}>{d.tailwinds}</td>
        <td className="px-3 py-2.5">{d.deck ? '📄' : ''}{d.dataroom ? '🗄' : ''}{!d.deck && !d.dataroom ? <span className="text-muted-2">—</span> : ''}</td>
        <td className="px-3 py-2.5">{d.log.length ? `💬 ${d.log.length}` : <span className="text-muted-2">—</span>}</td>
        <td className="px-3 py-2.5 text-muted">{d.dateCRM}</td>
      </tr>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Sourcing" title="Deals" desc={`${all.length} deals · ${live.length} live · ${fit} fit thesis · ${invested} done · ${dropped} not interested`}
        actions={<>
          <div className="flex gap-1.5">
            <button onClick={() => setView('table')} className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${view === 'table' ? 'bg-navy text-white border-navy' : 'bg-white text-muted border-line'}`}>▦ Table</button>
            <button onClick={() => setView('board')} className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${view === 'board' ? 'bg-navy text-white border-navy' : 'bg-white text-muted border-line'}`}>▤ Board</button>
          </div>
          <button onClick={exportCSV} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas">↓ Export CSV</button>
          <button onClick={() => navigate('/deals/new')} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d">＋ Add Deal</button>
        </>} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatTile label="Pipeline" value={live.length} note="live deals" accent="teal" />
        <StatTile label="Fit thesis" value={fit} note="live · pass hard filters" accent="green" />
        <StatTile label="Avg Krit score" value={avg} note="across live pipeline" accent="purple" />
        <StatTile label="Allocation sought" value={inr(totAlloc)} note="across live pipeline" accent="gold" />
        <StatTile label="Done" value={invested} note="invested · portfolio holdings" accent="navy" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center flex-wrap mb-3">
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setStatus('All')} className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${status === 'All' ? 'bg-navy text-white border-navy' : 'bg-white text-muted border-line'}`}>All ({all.length})</button>
          {PIPELINE_STATUSES.map((s) => {
            const n = all.filter((d) => d.status === s).length;
            if (!n) return null;
            return (
              <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${status === s ? 'bg-navy text-white border-navy' : 'bg-white text-muted border-line'}`}>{s} ({n})</button>
            );
          })}
        </div>
        {view === 'table' && (
          <button onClick={() => setGroup(group === 'status' ? 'none' : 'status')}
            className={`ml-auto px-3 py-1.5 rounded-full text-[12px] font-semibold border ${group === 'status' ? 'bg-teal-50 text-teal-d border-teal' : 'bg-white text-muted border-line'}`}>
            ☰ Group: {group === 'status' ? 'Status' : 'None'}
          </button>
        )}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${view === 'table' ? '' : 'ml-auto '}px-3 py-2 border border-line rounded-lg bg-white text-[13px]`} />
        <select value={sector} onChange={(e) => setSector(e.target.value)} className="px-3 py-2 border border-line rounded-lg bg-white text-[13px]">
          <option value="All">All sectors</option>
          {Object.keys(SECTORS).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={poc} onChange={(e) => setPoc(e.target.value)} className="px-3 py-2 border border-line rounded-lg bg-white text-[13px]">
          <option value="All">All POCs</option>
          {pocs.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div role="status" aria-busy="true" aria-label="Loading deals" className="overflow-hidden border border-line rounded-xl bg-white shadow-sh">
          <div className="flex gap-4 px-4 py-3 bg-navy">{Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} w={i === 1 ? 120 : 60} h={11} className="bg-white/20" />)}</div>
          {Array.from({ length: 8 }).map((_, r) => (
            <div key={r} className="flex gap-4 px-4 py-3 border-b border-line-2 items-center">
              {Array.from({ length: 7 }).map((_, c) => <Skeleton key={c} w={c === 1 ? 140 : 56} h={12} />)}
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-card border border-line rounded-xl p-10 text-center text-muted">
          <div className="text-[38px] opacity-40 mb-2">▦</div>
          <p>Couldn't load deals. Is the API running?</p>
          <p className="text-[11px] text-muted-2 mt-2">{String((error as Error).message)}</p>
        </div>
      ) : view === 'board' ? (
        <CrmBoard deals={filtered} onOpen={(id) => navigate(`/deals/${id}`)} canEdit={canEditDeals} />
      ) : (
        <div className="overflow-x-auto border border-line rounded-xl bg-white shadow-sh">
          <table className="border-collapse w-full text-[12px] whitespace-nowrap" style={{ minWidth: 1500 }}>
            <thead>
              <tr>
                <TH>#</TH><TH>Company</TH><TH>Status</TH><TH>Banker / MB</TH><TH>Krit POC</TH>
                <TH r>Rev FY26</TH><TH r>Rev FY25</TH><TH r>PAT FY26</TH><TH r>PAT FY25</TH>
                <TH r>Val ask</TH><TH r>Round</TH><TH r>Alloc</TH>
                <TH r>Impl P/E</TH><TH r>Peer P/E now</TH><TH r>Peer P/E @IPO</TH><TH r>MoS</TH>
                <TH>IPO</TH><TH r>Score</TH><TH>Credit</TH><TH>Fit</TH><TH>Listed peers</TH><TH>Sector tailwinds</TH><TH>Docs</TH><TH>Notes</TH><TH>Date</TH>
              </tr>
            </thead>
            {filtered.length === 0 ? (
              <tbody>
                <tr><td colSpan={COLS} className="text-center p-11 text-muted"><div className="text-[38px] opacity-40">▦</div>No deals match.</td></tr>
              </tbody>
            ) : group === 'none' ? (
              <tbody>{filtered.map((d, i) => row(d, i))}</tbody>
            ) : (
              groups.map((g, gi) => {
                const shut = collapsed.has(g.status);
                const alloc = g.deals.reduce((a, d) => a + (d.allocation || 0), 0);
                return (
                  <tbody key={g.status}>
                    {/* A tall band of empty white above the header is what actually separates one
                        group from the next — at this row density a tinted stripe alone reads as
                        just another row. */}
                    <tr>
                      <td colSpan={COLS} className={`px-3 pb-3 ${gi === 0 ? 'pt-3' : 'pt-9'}`}>
                        {/* Sticky so the group stays labelled while the table scrolls sideways. */}
                        <button onClick={() => toggleGroup(g.status)} aria-expanded={!shut}
                          className="sticky left-3 inline-flex items-center gap-2.5 text-left">
                          <span className={`text-muted-2 text-[11px] transition-transform ${shut ? '-rotate-90' : ''}`}>▼</span>
                          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] font-extrabold uppercase tracking-[0.6px] ${STATUS_PILL[g.status] ?? 'bg-[#eef1f5] text-muted'}`}>
                            <span className={`w-2 h-2 rounded-full ${STAGE_DOT[g.status] ?? 'bg-muted-2'}`} />
                            {g.status}
                          </span>
                          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#eef1f5] text-muted text-[11.5px] font-bold">{g.deals.length}</span>
                          {alloc > 0 && <span className="text-[11.5px] text-muted-2">{inr(alloc)} alloc</span>}
                        </button>
                      </td>
                    </tr>
                    {!shut && g.deals.map((d, i) => row(d, i))}
                  </tbody>
                );
              })
            )}
          </table>
        </div>
      )}
      <ChatWidget />
    </div>
  );
}
